import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client.js';
import { useAuth } from '../../store/auth.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OrbitalLoader } from '../../components/brand/OrbitalLoader.js';
import styles from './Computing.module.css';

interface ChartResp { status: 'pending' | 'ready' | 'failed'; sun?: string; moon?: string; ascendant?: string }

// Chart compute is normally 2–15s. 150s bounds the worst-case provider retry
// (~102s for one p-retry run) so a permanently-failing chart job surfaces a
// retry CTA instead of an indefinite spinner. /me/chart never emits `failed`
// today, so this timeout is the only failed-path once the avatar gate is gone.
const MAX_WAIT_MS = 150_000;

export function Computing(): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth(s => s.jwt);
  const nav = useNavigate();
  const [errorCount, setErrorCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!jwt || failed) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      if (Date.now() - startRef.current >= MAX_WAIT_MS) { setFailed(true); return; }
      try {
        const c = await apiFetch('/api/v1/me/chart', jwt).then(r => r.json() as Promise<ChartResp>);
        if (cancelled) return;
        setErrorCount(0);
        if (c.status === 'failed') { setFailed(true); return; }
        if (c.status === 'ready') { nav('/onboarding/info-reveal', { replace: true }); }
      } catch (err) {
        if (cancelled) return;
        console.error('[Computing] poll failed', err);
        setErrorCount(n => n + 1);
      }
    };
    void tick();
    const id = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [jwt, nav, failed]);

  return (
    <div className={styles.screen}>
      <div className={styles.backdrop}>
        <CosmosBackdrop />
      </div>

      <OrbitalLoader />

      <div className={styles.textOverlay}>
        {failed ? (
          <>
            <h2 className={styles.title}>{t('onboarding.computing.failedTitle')}</h2>
            <p className={styles.error}>{t('onboarding.computing.failedBody')}</p>
            <button
              type="button"
              className={styles.retry}
              onClick={() => { startRef.current = Date.now(); setFailed(false); setErrorCount(0); }}
            >
              {t('onboarding.computing.retry')}
            </button>
          </>
        ) : (
          <>
            <h2 className={styles.title}>{t('onboarding.computing.title')}</h2>
            {errorCount >= 3 && (
              <p className={styles.connectionWarning}>{t('onboarding.addingToWorld.errorRetry')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
